import type { SupportedLocale } from "staticConfiguration";
import {
  deDE as coreDe,
  enUS as coreEn,
  esES as coreEs,
  frFR as coreFr,
  type Localization,
} from "@mui/material/locale";
import {
  createTheme,
  responsiveFontSizes,
  type ThemeOptions,
  ThemeProvider,
} from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import {
  deDE as gridDe,
  enUS as gridEn,
  esES as gridEs,
  frFR as gridFr,
} from "@mui/x-data-grid-pro/locales";
import {
  deDE as datePickersDe,
  enUS as datePickersEn,
  esES as datePickersEs,
  frFR as datePickersFr,
} from "@mui/x-date-pickers-pro/locales";
import type { BrandCode } from "core/entities";
import { LocaleOverrideContext } from "core/hooks/LocaleOverrideContext";
import { useLocale } from "core/hooks/useLocale";
import { BaseThemeObject } from "core/theme/BaseThemeObject";
import { getBrand } from "core/utils/getBrand";
import { memo, type ReactNode, useContext, useMemo } from "react";

const localeMap: Record<SupportedLocale, Localization> = {
  en: deepmerge(deepmerge(coreEn, gridEn), datePickersEn),
  fr: deepmerge(deepmerge(coreFr, gridFr), datePickersFr),
  "de-DE": deepmerge(deepmerge(coreDe, gridDe), datePickersDe),
  es: deepmerge(deepmerge(coreEs, gridEs), datePickersEs),
};

export const MUIThemeAndLocalizationProvider = memo(
  ({
    children,
    brandCode,
    customTheme,
  }: {
    children: ReactNode;
    brandCode?: BrandCode;
    customTheme?: ThemeOptions | null;
  }) => {
    const overrideLocale = useContext(LocaleOverrideContext);
    const { themeOptions: theme } = getBrand(brandCode);
    const routerLocale = useLocale();
    const locale = (overrideLocale || routerLocale) as SupportedLocale;
    const localizationResource = localeMap[locale];
    const currentTheme = useMemo(() => {
      let baseThemeOptions = deepmerge(BaseThemeObject, theme);
      if (customTheme) {
        baseThemeOptions = deepmerge(BaseThemeObject, customTheme);
      }
      const themeWithLocalization = createTheme(
        { cssVariables: true, ...baseThemeOptions },
        localizationResource
      );
      const responsiveTypographyTheme = responsiveFontSizes(
        themeWithLocalization
      );
      return responsiveTypographyTheme;
    }, [theme, customTheme, localizationResource]);

    return <ThemeProvider theme={currentTheme}>{children}</ThemeProvider>;
  }
);
MUIThemeAndLocalizationProvider.displayName = "MUIThemeAndLocalizationProvider";
